/* QH Management — main app shell */
const { useState: useStateA, useEffect: useEffectA } = React;

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "palette": "vault",
  "logoVariant": "geometric",
  "theme": "light",
  "density": "default"
}/*EDITMODE-END*/;

function App() {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [authed, setAuthed] = useStateA(false);
  const [bootstrapping, setBootstrapping] = useStateA(true);
  const [, forceTick] = useStateA(0);
  const [page, setPage] = useStateA('dashboard');
  const [collapsed, setCollapsed] = useStateA(false);
  const [txModalScheme, setTxModalScheme] = useStateA(null);
  const [toasts, setToasts] = useStateA([]);
  const [userMenuOpen, setUserMenuOpen] = useStateA(false);
  const [showChangePassword, setShowChangePassword] = useStateA(false);
  const [updateAvailable, setUpdateAvailable] = useStateA(false);

  useEffectA(() => {
    document.documentElement.setAttribute('data-palette', tweaks.palette || 'vault');
    document.documentElement.setAttribute('data-theme', tweaks.theme || 'light');
    document.documentElement.setAttribute('data-density', tweaks.density || 'default');
  }, [tweaks]);

  // Try a silent refresh on cold load — if a refresh cookie is still valid we
  // skip the login screen entirely. Either way, end the bootstrapping splash.
  useEffectA(() => {
    (async () => {
      try {
        const ok = await window.QH_API.tryRefresh();
        if (ok) {
          await window.QH_STORE.loadAll();
          setAuthed(true);
        }
      } finally { setBootstrapping(false); }
    })();
  }, []);

  // Re-render whenever the store fires a bump (after loadAll / mutations).
  useEffectA(() => window.QH_STORE.subscribe(() => forceTick(t => t + 1)), []);

  // Auto-update detection. Captures the server's reported version on
  // first paint, then polls every 5 minutes (and on window focus) for
  // changes. When the server's `version` or `startedAt` differs, we
  // surface a banner with a Reload button — that fetches the new
  // bundle. The polling is best-effort: any network error is silently
  // ignored so an offline browser doesn't churn.
  useEffectA(() => {
    let baseline = null;
    let cancelled = false;
    const fetchVersion = async () => {
      try {
        const res = await fetch('/api/v1/version', { cache: 'no-store' });
        if (!res.ok) return;
        const v = await res.json();
        if (cancelled) return;
        if (!baseline) { baseline = v; return; }
        if (v.version !== baseline.version || v.startedAt !== baseline.startedAt) {
          setUpdateAvailable(true);
        }
      } catch { /* offline / network blip — try again next tick */ }
    };
    fetchVersion();
    const id = setInterval(fetchVersion, 5 * 60 * 1000);
    const onFocus = () => fetchVersion();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const go = (p) => {
    setPage(p);
    if (p.startsWith('scheme:')) setPage(p);
    window.scrollTo(0, 0);
  };

  const pushToast = (msg, kind = 'info') => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, kind }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  };

  const onTxCreated = (tx) => {
    pushToast(`${tx.type === 'deposit' ? 'Deposit' : 'Withdrawal'} of ${fmtBHD(tx.amount)} BHD recorded`, 'pos');
  };

  const handleLogout = async (reason) => {
    try { await window.QH_API.post('/auth/logout'); } catch {}
    window.QH_API.clear();
    setAuthed(false);
    setPage('dashboard');
    if (reason === 'idle') pushToast('Signed out — 15 minutes of inactivity.', 'info');
    else if (reason === 'expired') pushToast('Session expired — please sign in again.', 'info');
  };

  // ── Idle timeout ──────────────────────────────────────────────────
  // 15 minutes of no user activity → auto-logout. Activity is any
  // mouse/keyboard/touch interaction on the document. We reset a single
  // timer rather than checking timestamps on a poll so the cost stays
  // O(1) per event. The timer is armed only while `authed` is true.
  useEffectA(() => {
    if (!authed) return;
    const IDLE_MS = 15 * 60 * 1000;
    let timer = null;
    let logging = false;
    const fire = () => {
      if (logging) return;
      logging = true;
      handleLogout('idle');
    };
    const reset = () => {
      if (logging) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, IDLE_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'focus'];
    for (const ev of events) window.addEventListener(ev, reset, { passive: true });
    reset();
    return () => {
      if (timer) clearTimeout(timer);
      for (const ev of events) window.removeEventListener(ev, reset);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // Bootstrap-time fallback: if the user landed on `dashboard` but their
  // role doesn't have dashboard:read, redirect to the first page they CAN
  // see. Hooks must run unconditionally on every render — placing this
  // above the early returns avoids React error #310. The candidate list
  // is hard-coded (no QH_DATA dependency) so it's safe to call before the
  // store has finished loading.
  useEffectA(() => {
    if (!authed) return;
    if (page !== 'dashboard') return;
    if (window.can?.('dashboard', 'read')) return;
    const candidates = [
      ['members', 'members'],
      ['memberStatement', 'member_statement'],
      ['memberBalances', 'member_balances'],
      ['schemes', 'schemes'],
      ['events', 'events'],
      ['transactions', 'transactions'],
      ['finance', 'finance_apps'],
      ['repayments', 'repayments'],
      ['banks', 'banks'],
      ['rbac', 'users'],
      ['templates', 'templates'],
      ['audit', 'audit'],
      ['import', 'data_import'],
      ['settings', 'settings'],
    ];
    for (const [pageId, req] of candidates) {
      if (window.can?.(req, 'read')) { setPage(pageId); return; }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, page, QH_DATA.myPerms]);

  if (bootstrapping) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',fontFamily:'var(--font-ui)',color:'var(--ink-3)',fontSize:13}}>Loading…</div>;
  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} palette={tweaks.palette} logoVariant={tweaks.logoVariant}/>;

  // `requires` names a page id from the RBAC matrix — the sidebar hides any
  // item whose required page the role can't read. Items without `requires`
  // (currently just Dashboard) are visible to every authenticated user.
  // Data Import has no dedicated page in the matrix; we surface it to anyone
  // who can create or edit anything import-capable, leaving the per-kind
  // permission gates inside the wizard to do the actual blocking.
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <I.Home/>, group: 'Overview', requires: 'dashboard' },
    { id: 'members', label: 'Members', icon: <I.Users/>, badge: QH_DATA.activeMembersTotal ?? QH_DATA.members?.filter(m => m.active).length, group: 'Directory', requires: 'members' },
    { id: 'banks', label: 'Banks', icon: <I.Banknote/>, badge: QH_DATA.banks?.filter(b => b.active).length, group: 'Directory', requires: 'banks' },
    { id: 'memberStatement', label: 'Member Statement', icon: <I.ScrollText/>, group: 'Directory', requires: 'member_statement' },
    { id: 'memberBalances', label: 'Member Balances', icon: <I.TrendingUp/>, group: 'Directory', requires: 'member_balances' },
    { id: 'schemes', label: 'Schemes', icon: <I.Vault/>, badge: QH_DATA.schemes.filter(s => s.status === 'active').length, group: 'Schemes', requires: 'schemes' },
    { id: 'events', label: 'Events', icon: <I.Calendar/>, badge: QH_DATA.events.length, group: 'Schemes', requires: 'events' },
    { id: 'transactions', label: 'Transactions', icon: <I.TrendingUp/>, group: 'Schemes', requires: 'transactions' },
    { id: 'finance', label: 'Finance Apps', icon: <I.Hand/>, badge: QH_DATA.financeApps.filter(a => a.status === 'pending').length, group: 'Personal Finance', requires: 'finance_apps' },
    { id: 'repayments', label: 'Repayments', icon: <I.Coins/>, group: 'Personal Finance', requires: 'repayments' },
    { id: 'rbac', label: 'Users & Roles', icon: <I.Shield/>, group: 'Admin', requires: 'users' },
    { id: 'templates', label: 'Notifications', icon: <I.MessageSquare/>, group: 'Admin', requires: 'templates' },
    { id: 'audit', label: 'Audit Log', icon: <I.ScrollText/>, group: 'Admin', requires: 'audit' },
    { id: 'import', label: 'Data Import', icon: <I.Download/>, group: 'Admin', requires: 'data_import' },
    { id: 'settings', label: 'Settings', icon: <I.Settings/>, group: 'Admin', requires: 'settings' },
    { id: 'security', label: 'Security', icon: <I.Lock/>, group: 'Admin', requires: 'security' },
  ];

  const groups = ['Overview', 'Directory', 'Schemes', 'Personal Finance', 'Admin'];
  const isSchemeDetail = page.startsWith('scheme:');
  const isEventDetail  = page.startsWith('event:');
  const isEventTx      = page.startsWith('eventTx:');
  const isMemberProfile = page.startsWith('member:');
  const isMemberStatement = page === 'memberStatement' || page.startsWith('memberStatement:');
  const memberStatementParams = isMemberStatement && page.includes(':') ? page.split(':').slice(1) : [];
  const isFinanceWizard = page.startsWith('financeWizard:');
  const financeWizardArg = isFinanceWizard ? page.split(':').slice(1).join(':') : null;
  const isFinanceApp = page.startsWith('financeApp:');
  const financeAppArg = isFinanceApp ? page.split(':').slice(1).join(':') : null;
  const currentNavId = isSchemeDetail ? 'schemes'
    : (isEventDetail || isEventTx) ? 'events'
    : isMemberProfile ? 'members'
    : isMemberStatement ? 'memberStatement'
    : (isFinanceWizard || isFinanceApp) ? 'finance'
    : page;

  return (
    <div className="app" data-collapsed={collapsed}>
      {updateAvailable && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          background: 'var(--accent)', color: 'white', padding: '8px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 12, fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <span>A new version of QH Management is available.</span>
          <button className="btn" style={{height: 26, padding: '0 12px', background: 'white', color: 'var(--accent)', fontWeight: 600}}
                  onClick={() => window.location.reload()}>
            Reload now
          </button>
          <button className="icon-btn" onClick={() => setUpdateAvailable(false)} style={{color: 'white'}} title="Dismiss until next change"><I.X s={12}/></button>
        </div>
      )}
      <div className="topbar">
        <div className="topbar-brand">
          <QHLogo variant={tweaks.logoVariant} palette={tweaks.palette} size={26}/>
        </div>
        <SearchPalette go={go}/>
        <div className="topbar-spacer"/>
        <div className="topbar-actions">
          <button className="icon-btn" title="Toggle theme" onClick={() => setTweak('theme', tweaks.theme === 'dark' ? 'light' : 'dark')}>
            {tweaks.theme === 'dark' ? <I.Sun/> : <I.Moon/>}
          </button>
          <button className="icon-btn" title="Notifications">
            <I.Bell/><span className="dot"/>
          </button>
          <button className="icon-btn" onClick={() => go('rbac')}><I.Settings/></button>
          <div style={{width: 1, height: 20, background: 'var(--line)', margin: '0 4px'}}/>
          <div style={{position: 'relative'}}>
            <button className="user-chip" onClick={() => setUserMenuOpen(o => !o)} title="Account">
              <span className="avatar">{QH_DATA.currentUser.avatarInitials}</span>
              <span style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1}}>
                <span style={{fontWeight: 500, fontSize: 12}}>{QH_DATA.currentUser.name}</span>
                <span className="text-xs muted">{QH_DATA.activeRole?.name || ''} · QH HQ</span>
              </span>
              <I.ChevronDown s={11}/>
            </button>
            {userMenuOpen && (
              <>
                {/* Click-outside catcher — closes the menu without
                    having to wire onMouseDown handlers on every button. */}
                <div onClick={() => setUserMenuOpen(false)}
                     style={{position: 'fixed', inset: 0, zIndex: 30}}/>
                <div style={{position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 31,
                             background: 'var(--bg-elev)', border: '1px solid var(--line)',
                             borderRadius: 8, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.12)'}}>
                  <div style={{padding: '10px 12px', borderBottom: '1px solid var(--line)'}}>
                    <div style={{fontSize: 12, fontWeight: 600}}>{QH_DATA.currentUser.name}</div>
                    <div className="text-xs muted">{QH_DATA.currentUser.email}</div>
                  </div>
                  <button className="nav-item" style={{borderRadius: 0, width: '100%'}}
                          onClick={() => { setUserMenuOpen(false); setShowChangePassword(true); }}>
                    <I.Lock s={13}/><span>Change password</span>
                  </button>
                  <button className="nav-item" style={{borderRadius: 0, width: '100%'}}
                          onClick={() => { setUserMenuOpen(false); handleLogout(); }}>
                    <I.Logout s={13}/><span>Sign out</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="sidebar">
        {groups.map(g => {
          // Items can declare a `requires` page id — hide them unless the
          // current role has `read` permission on that page.
          const visible = navItems.filter(n => n.group === g && (!n.requires || window.can(n.requires, 'read')));
          if (visible.length === 0) return null;
          return (
            <div key={g} className="nav-group">
              <div className="nav-group-title">{g}</div>
              {visible.map(n => (
                <button key={n.id} className={`nav-item ${currentNavId === n.id ? 'active' : ''}`} onClick={() => go(n.id)}>
                  {n.icon}<span>{n.label}</span>
                  {n.badge != null && <span className="badge">{n.badge}</span>}
                </button>
              ))}
            </div>
          );
        })}
      </div>

      <div className="main">
        {page === 'dashboard' && <Dashboard go={go} role={QH_DATA.activeRole?.id}/>}
        {page === 'schemes' && <SchemesPage go={go}/>}
        {isSchemeDetail && <SchemeDetailPage schemeId={page.split(':')[1]} go={go} openTxModal={setTxModalScheme}/>}
        {page === 'events' && <EventsPage go={go}/>}
        {isEventDetail && <EventDetailPage eventId={page.split(':')[1]} go={go}/>}
        {isEventTx && <EventTransactionPage eventId={page.split(':')[1]} go={go}/>}
        {page === 'transactions' && <TransactionsPage openTxModal={() => setTxModalScheme(QH_DATA.schemes[0].id)}/>}
        {page === 'finance' && <FinancePage go={go}/>}
        {isFinanceWizard && <FinanceWizardPage arg={financeWizardArg} go={go}/>}
        {isFinanceApp && <window.FinanceApplicationPage appId={financeAppArg} go={go}/>}
        {page === 'repayments' && <RepaymentsPage/>}
        {page === 'members' && <MembersPage go={go}/>}
        {isMemberProfile && (window.can?.('member_profile', 'read')
          ? <MemberProfilePage itsNo={page.split(':')[1]} go={go}/>
          : <div className="page-body"><div style={{padding:24,color:'var(--neg)'}}>You don't have access to Member Profile.</div></div>)}
        {page === 'banks' && <BanksPage/>}
        {isMemberStatement && <MemberStatementPage params={memberStatementParams}/>}
        {page === 'memberBalances' && <MemberBalancesPage go={go}/>}
        {page === 'rbac' && <RBACPage/>}
        {page === 'audit' && <AuditPage/>}
        {page === 'templates' && <TemplatesPage/>}
        {page === 'import' && <ImportPage/>}
        {page === 'settings' && <SettingsPage/>}
        {page === 'security' && <SecurityPage/>}
      </div>

      {txModalScheme && <TransactionModal schemeId={txModalScheme} onClose={() => setTxModalScheme(null)} onCreated={onTxCreated}/>}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} onSuccess={() => { setShowChangePassword(false); pushToast('Password updated.', 'pos'); }}/>}

      <div className="toast-stack">
        {toasts.map(t => (
          <div key={t.id} className="toast">
            <span className="tag pos"><I.Check s={10}/></span>
            <span>{t.msg}</span>
          </div>
        ))}
      </div>

      <TweaksPanel title="Tweaks">
        <TweakSection title="Logo & Palette" subtitle="Tap a card to switch the brand identity">
          <LogoSelectorPanel palette={tweaks.palette} setPalette={(v) => setTweak('palette', v)} logoVariant={tweaks.logoVariant} setLogoVariant={(v) => setTweak('logoVariant', v)}/>
        </TweakSection>
        <TweakSection title="Appearance">
          <TweakRadio label="Theme" options={[{label: 'Light', value: 'light'}, {label: 'Dark', value: 'dark'}]} value={tweaks.theme} onChange={(v) => setTweak('theme', v)}/>
          <TweakRadio label="Density" options={[{label: 'Cozy', value: 'cozy'}, {label: 'Default', value: 'default'}, {label: 'Spacious', value: 'spacious'}]} value={tweaks.density} onChange={(v) => setTweak('density', v)}/>
        </TweakSection>
        <TweakSection title="Logo variants only" subtitle="Mix any variant with any palette">
          <TweakRadio label="Mark" options={[{label: 'Geometric', value: 'geometric'}, {label: 'Abstract', value: 'abstract'}, {label: 'Classic', value: 'classic'}]} value={tweaks.logoVariant} onChange={(v) => setTweak('logoVariant', v)}/>
          <TweakRadio label="Palette" options={[{label: 'Vault', value: 'vault'}, {label: 'Pulse', value: 'pulse'}, {label: 'Grove', value: 'grove'}]} value={tweaks.palette} onChange={(v) => setTweak('palette', v)}/>
        </TweakSection>
      </TweaksPanel>
    </div>
  );
}

// ============ Events page (cross-scheme) ============
function EventsPage({ go }) {
  const [showCreate, setShowCreate] = useStateA(false);
  const [from, setFrom] = useStateA('');
  const [to, setTo] = useStateA('');
  const [schemeId, setSchemeId] = useStateA('');

  // Client-side filtering — event dates are ISO "YYYY-MM-DD", so plain
  // string comparison against the date inputs gives correct chronological
  // ordering without a Date() round-trip.
  const filtered = QH_DATA.events.filter(e => {
    if (schemeId && e.schemeId !== schemeId) return false;
    const d = (e.date || '').slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
  const filterActive = !!(from || to || schemeId);
  const clearFilters = () => { setFrom(''); setTo(''); setSchemeId(''); };

  // Sort schemes for the dropdown by code so operators can find them
  // quickly by prefix (HS, MS, TS, …).
  const schemesForFilter = [...(QH_DATA.schemes || [])]
    .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Schemes</span><I.ChevronRight s={10}/><span>Events</span></div>
          <h1>Events</h1>
          <div className="subtitle">
            {filterActive
              ? <>{filtered.length} of {QH_DATA.events.length} events · filtered</>
              : <>Group transactions under named events for reporting & reconciliation.</>}
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => window.QH_STORE.reload()} title="Reload data"><I.Refresh/></button>
          <button className="btn" onClick={() => window.safeDownload('/events/export.csv', 'events.csv')}><I.Download/> Export</button>
          {window.can('events','create') && <button className="btn primary" onClick={() => setShowCreate(true)}><I.Plus/> New event</button>}
        </div>
      </div>
      {showCreate && <EventCreateModal onClose={() => setShowCreate(false)}/>}

      <div className="toolbar">
        <label className="text-xs muted" style={{whiteSpace:'nowrap'}}>From</label>
        <input className="input font-mono" style={{height:28, width:140}} type="date"
               value={from} onChange={e => setFrom(e.target.value)}/>
        <label className="text-xs muted" style={{whiteSpace:'nowrap'}}>To</label>
        <input className="input font-mono" style={{height:28, width:140}} type="date"
               value={to} onChange={e => setTo(e.target.value)}/>
        <div className="sep"/>
        <label className="text-xs muted" style={{whiteSpace:'nowrap'}}>Scheme</label>
        <select className="select" style={{height:28, minWidth:200}}
                value={schemeId} onChange={e => setSchemeId(e.target.value)}>
          <option value="">All schemes</option>
          {schemesForFilter.map(s => (
            <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
          ))}
        </select>
        {filterActive && <button className="btn sm ghost" onClick={clearFilters} title="Clear all filters">Clear</button>}
        <div className="grow"/>
        <span className="text-xs muted" style={{whiteSpace:'nowrap'}}>
          {filtered.length} of {QH_DATA.events.length} events
        </span>
      </div>

      <div className="page-body">
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Event</th><th>Scheme</th><th>Date</th><th>Status</th><th className="right">Transactions</th><th className="right">Deposits</th><th className="right">Withdrawals</th><th className="right">Net · BHD</th><th></th></tr></thead>
            <tbody>
              {filtered.map(e => {
                const sch = QH_DATA.schemes.find(s => s.id === e.schemeId);
                const closed = e.status === 'closed';
                return (
                  <tr key={e.id} className="clickable" onClick={() => go('event:' + e.id)}>
                    <td><div style={{fontWeight: 500}}>{e.name}</div><div className="text-xs muted font-mono">{e.id.toUpperCase()}</div></td>
                    <td><div style={{fontSize: 12}}>{sch?.name}</div><div className="text-xs muted font-mono">{sch?.code}</div></td>
                    <td className="font-mono text-xs">{window.fmtDate(e.date)}</td>
                    <td><span className={`tag ${closed ? '' : 'pos'}`}>{closed ? 'closed' : 'open'}</span></td>
                    <td className="num right">{e.transactions}</td>
                    <td className="num right pos">+{fmtBHD(e.deposits)}</td>
                    <td className="num right neg">−{fmtBHD(e.withdrawals)}</td>
                    <td className="num right" style={{fontWeight: 600}}>{fmtBHD(e.deposits - e.withdrawals)}</td>
                    <td><I.ChevronRight s={12}/></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>
                    {filterActive ? 'No events match these filters.' : 'No events yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ============ Transactions cross-scheme page ============
function TransactionsPage({ openTxModal }) {
  const [filter, setFilter] = useStateA('all');
  const [q, setQ] = useStateA('');
  const txs = QH_DATA.transactions.filter(t => {
    if (filter !== 'all' && t.type !== filter) return false;
    if (q && !`${t.member} ${t.id} ${t.bank}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const activeBanks = (QH_DATA.banks || []).filter(b => b.active);
  // Deposit/withdrawal totals come from the server's transaction aggregate
  // (all transactions, every bank incl. free-text/blank/inactive) rather
  // than summing active-bank balances, which silently dropped those rows
  // and showed +0 for an operator with an empty bank set.
  const totalDep = QH_DATA.transactionsDeposits ?? 0;
  const totalWd  = QH_DATA.transactionsWithdrawals ?? 0;
  const txnTotal = QH_DATA.transactionsTotal ?? QH_DATA.transactions.length;
  const activeBankNames = activeBanks.map(b => b.code || b.name).join(', ');
  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Schemes</span><I.ChevronRight s={10}/><span>Deposits & Withdrawals</span></div>
          <h1>Transactions</h1>
          <div className="subtitle">{txnTotal} transactions · all schemes</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => window.safeDownload('/transactions/export.csv', 'transactions.csv')}><I.Download/> Export</button>
          {window.can('transactions','create') && <button className="btn primary" onClick={openTxModal}><I.Plus/> Record transaction</button>}
        </div>
      </div>
      <div className="page-body" style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <KPIStrip items={[
          { label: 'Total deposits', value: '+' + fmtBHD(totalDep) + ' BHD' },
          { label: 'Total withdrawals', value: '−' + fmtBHD(totalWd) + ' BHD' },
          { label: 'Net flow', value: fmtBHD(totalDep - totalWd) + ' BHD' },
          { label: 'Active banks', value: String(activeBanks.length), sub: activeBankNames || '—' },
        ]}/>
        <div className="toolbar" style={{borderRadius: 'var(--radius) var(--radius) 0 0', border: '1px solid var(--line)', borderBottom: 'none'}}>
          <div style={{display: 'flex', gap: 4}}>
            {[['all', 'All'], ['deposit', 'Deposits'], ['withdrawal', 'Withdrawals']].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} className={`btn sm ${filter === k ? 'accent' : 'ghost'}`}>{l}</button>
            ))}
          </div>
          <div className="sep"/>
          <input className="input" style={{height: 28, width: 220}} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}/>
          <div className="grow"/>
          <button className="btn sm ghost"><I.Filter/> Date · Bank · Status</button>
        </div>
        <div className="table-wrap" style={{borderTopLeftRadius: 0, borderTopRightRadius: 0, marginTop: -12}}>
          <TransactionsTable txs={txs} compact={false}/>
        </div>
      </div>
    </>
  );
}

// ============ Repayments page ============
function RepaymentsPage() {
  const [tab, setTab] = useStateA('all');
  const [clearing, setClearing] = useStateA(null); // repayment row being cleared
  const counts = {
    all: QH_DATA.repayments.length,
    pending: QH_DATA.repayments.filter(r => r.status === 'pending').length,
    cleared: QH_DATA.repayments.filter(r => r.status === 'cleared').length,
    overdue: QH_DATA.repayments.filter(r => r.status === 'overdue' || r.status === 'bounced').length,
  };
  const filtered = QH_DATA.repayments.filter(r => {
    if (tab === 'all') return true;
    if (tab === 'overdue') return r.status === 'overdue' || r.status === 'bounced';
    return r.status === tab;
  });
  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Personal Finance</span><I.ChevronRight s={10}/><span>Repayments</span></div>
          <h1>Repayment Tracking</h1>
          <div className="subtitle">{counts.pending} cheques pending deposit · {counts.overdue} require attention</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => window.safeDownload('/repayments/export.csv', 'repayments.csv')}><I.Download/> Export schedule</button>
        </div>
      </div>
      <div className="page-body" style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <div className="tabs" style={{borderRadius: 'var(--radius) var(--radius) 0 0', border: '1px solid var(--line)', borderBottom: 'none'}}>
          {[['all', 'All'], ['pending', 'Pending'], ['cleared', 'Cleared'], ['overdue', 'Overdue / bounced']].map(([k, l]) => (
            <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}<span className="count">{counts[k]}</span></button>
          ))}
        </div>
        <div className="table-wrap" style={{borderTopLeftRadius: 0, borderTopRightRadius: 0, marginTop: -12}}>
          <table className="data">
            <thead><tr><th>Applicant</th><th>App ID</th><th>Cheque</th><th>Bank</th><th>Due date</th><th className="right">Amount · BHD</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="clickable">
                  <td>{r.applicant}</td>
                  <td className="font-mono text-xs">{r.appId.toUpperCase()}</td>
                  <td className="font-mono text-xs">{r.cheque}</td>
                  <td>{r.bank}</td>
                  <td className="font-mono text-xs">{window.fmtDate(r.dueDate)}</td>
                  <td className="num right" style={{fontWeight: 600}}>{fmtBHD(r.amount)}</td>
                  <td><span className={`tag ${r.status === 'cleared' ? 'pos' : r.status === 'pending' ? 'info' : 'neg'}`}><span className="dot"/>{r.status}</span></td>
                  <td style={{display:'flex',gap:4}}>
                    {r.status !== 'cleared' && <button className="btn sm" title="Mark cheque cleared" onClick={() => setClearing(r)}><I.Check s={11}/></button>}
                    {r.status !== 'bounced' && r.status !== 'cleared' && <button className="btn sm danger" title="Mark bounced" onClick={async () => { if(!window.confirm('Mark cheque as bounced?')) return; try { await window.QH_API.post(`/repayments/${r.id}/mark-bounced`); await window.QH_STORE.reload(); } catch (e) { alert(e.message); } }}><I.X s={11}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DefaultersPanel/>
      </div>
      {clearing && (
        <window.ClearChequeModal
          title="Mark cheque cleared"
          subtitle={`Repayment ${clearing.cheque} · ${clearing.applicant}`}
          cheque={{ issuer: clearing.applicant, cheque: clearing.cheque, bank: clearing.bank, dueDate: clearing.dueDate, amount: clearing.amount }}
          onClose={() => setClearing(null)}
          onSubmit={async ({ clearedOn, depositBankId, comment }) => {
            await window.QH_API.post(`/repayments/${clearing.id}/mark-deposited`, { clearedOn, depositBankId, comment });
            await window.QH_STORE.reload();
          }}/>
      )}
    </>
  );
}

window.App = App;

ReactDOM.createRoot(document.getElementById('root')).render(<App/>);
